import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Phone, Clock, User, ChevronRight, Play, FileText, Timer, Volume2, Pause, Download, SkipBack, SkipForward } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CallLookups } from '../components/connections/CallLookups';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Audio Player Component
const AudioPlayer = ({ recordingUrl, duration, callId }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration ? duration / 1000 : 0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const handlePlayPause = () => {
    if (!audioRef.current) {
      setIsLoading(true);
      const audio = new Audio(recordingUrl);
      audio.onloadedmetadata = () => {
        setAudioDuration(audio.duration);
        setIsLoading(false);
      };
      audio.ontimeupdate = () => {
        setCurrentTime(audio.currentTime);
      };
      audio.onended = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };
      audio.onerror = () => {
        toast.error('Unable to load recording');
        setIsLoading(false);
      };
      audioRef.current = audio;
      audio.play();
      setIsPlaying(true);
    } else {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * audioDuration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleSkip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(audioDuration, audioRef.current.currentTime + seconds));
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 border border-cyan-500/20">
      <div className="flex items-center gap-4 mb-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
          <Volume2 className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">Call Recording</p>
          <p className="text-xs text-muted-foreground">
            {formatTime(currentTime)} / {formatTime(audioDuration)}
          </p>
        </div>
        <a 
          href={recordingUrl} 
          download={`call-recording-${callId}.mp3`}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          title="Download Recording"
        >
          <Download className="w-4 h-4 text-muted-foreground hover:text-foreground" />
        </a>
      </div>

      {/* Progress Bar */}
      <div 
        className="h-2 bg-white/10 rounded-full cursor-pointer mb-3 overflow-hidden"
        onClick={handleSeek}
        data-testid="audio-progress-bar"
      >
        <div 
          className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Waveform Visualization (decorative) */}
      <div className="flex items-center justify-center gap-0.5 h-8 mb-3">
        {Array.from({ length: 40 }).map((_, i) => {
          const isActive = (i / 40) * 100 < progress;
          const height = Math.sin(i * 0.5) * 12 + 16 + Math.random() * 4;
          return (
            <div
              key={i}
              className={`w-1 rounded-full transition-all duration-150 ${
                isActive ? 'bg-cyan-500' : 'bg-white/20'
              }`}
              style={{ height: `${height}px` }}
            />
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleSkip(-10)}
          className="text-muted-foreground hover:text-foreground"
          disabled={!audioRef.current}
        >
          <SkipBack className="w-4 h-4" />
        </Button>
        
        <Button
          onClick={handlePlayPause}
          disabled={isLoading}
          className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 shadow-lg"
          data-testid="play-pause-btn"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-5 h-5 text-white" />
          ) : (
            <Play className="w-5 h-5 text-white ml-0.5" />
          )}
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleSkip(10)}
          className="text-muted-foreground hover:text-foreground"
          disabled={!audioRef.current}
        >
          <SkipForward className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const CallHistory = () => {
  const { token, user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDetails, setCallDetails] = useState(null);
  const [timeFilter, setTimeFilter] = useState('daily');
  const [playingRecording, setPlayingRecording] = useState(null);
  const [audioRef, setAudioRef] = useState(null);

  useEffect(() => {
    fetchCalls();
  }, [token]);

  // Calculate total minutes based on filter
  const filteredStats = useMemo(() => {
    const now = new Date();
    const filterCalls = calls.filter(call => {
      if (!call.start_timestamp) return false;
      const callDate = new Date(call.start_timestamp);
      
      switch (timeFilter) {
        case 'daily':
          return callDate.toDateString() === now.toDateString();
        case 'weekly':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return callDate >= weekAgo;
        case 'monthly':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return callDate >= monthAgo;
        case 'yearly':
          const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          return callDate >= yearAgo;
        default:
          return true;
      }
    });

    const totalMs = filterCalls.reduce((sum, call) => sum + (call.duration_ms || 0), 0);
    const totalSeconds = Math.floor(totalMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;

    return {
      calls: filterCalls.length,
      totalMinutes,
      remainingSeconds,
      totalMs
    };
  }, [calls, timeFilter]);

  const fetchCalls = async () => {
    try {
      const res = await axios.get(`${API}/business/my-calls?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalls(res.data.calls || []);
    } catch (err) {
      console.error('Failed to fetch calls:', err);
      toast.error('Failed to load call history');
    } finally {
      setLoading(false);
    }
  };

  const viewCallDetails = async (callId) => {
    setSelectedCall(callId);
    try {
      const res = await axios.get(`${API}/retell/call/${callId}`);
      setCallDetails(res.data);
    } catch (err) {
      console.error('Failed to fetch call details:', err);
      toast.error('Failed to load call details');
      setSelectedCall(null);
    }
  };

  const playRecording = (recordingUrl, callId) => {
    if (playingRecording === callId) {
      // Stop playing
      if (audioRef) {
        audioRef.pause();
        audioRef.currentTime = 0;
      }
      setPlayingRecording(null);
      return;
    }

    // Start playing
    if (recordingUrl) {
      const audio = new Audio(recordingUrl);
      audio.onended = () => setPlayingRecording(null);
      audio.onerror = () => {
        toast.error('Unable to play recording');
        setPlayingRecording(null);
      };
      audio.play();
      setAudioRef(audio);
      setPlayingRecording(callId);
    } else {
      toast.error('Recording not available');
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ended': return 'text-green-400 bg-green-400/20';
      case 'ongoing': return 'text-blue-400 bg-blue-400/20';
      case 'error': return 'text-red-400 bg-red-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground tracking-tight">CALL HISTORY</h1>
          <p className="text-muted-foreground mt-1">All calls handled by your Pro</p>
        </div>

        {/* Total Minutes Card with Filter */}
        <Card className="card-modern p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Timer className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Total Minutes Spoken</p>
                <p className="text-4xl font-bold text-orange-400">
                  {filteredStats.totalMinutes}<span className="text-xl">m</span> {filteredStats.remainingSeconds}<span className="text-xl">s</span>
                </p>
                <p className="text-muted-foreground text-xs">{filteredStats.calls} calls</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {['daily', 'weekly', 'monthly', 'yearly'].map((filter) => (
                <Button
                  key={filter}
                  variant={timeFilter === filter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTimeFilter(filter)}
                  className={timeFilter === filter 
                    ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                    : 'border-border text-muted-foreground hover:text-foreground'}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="card-modern p-4">
            <p className="text-muted-foreground text-sm">Total Calls</p>
            <p className="text-3xl font-bold text-foreground">{calls.length}</p>
          </Card>
          <Card className="card-modern p-4">
            <p className="text-muted-foreground text-sm">Completed</p>
            <p className="text-3xl font-bold text-green-400">{calls.filter(c => c.call_status === 'ended').length}</p>
          </Card>
          <Card className="card-modern p-4">
            <p className="text-muted-foreground text-sm">Avg Duration</p>
            <p className="text-3xl font-bold text-blue-400">
              {formatDuration(calls.reduce((a, c) => a + (c.duration_ms || 0), 0) / calls.length || 0)}
            </p>
          </Card>
          <Card className="card-modern p-4">
            <p className="text-muted-foreground text-sm">Today</p>
            <p className="text-3xl font-bold text-purple-400">
              {calls.filter(c => {
                const today = new Date().toDateString();
                return new Date(c.start_timestamp).toDateString() === today;
              }).length}
            </p>
          </Card>
        </div>

        {/* Calls List */}
        <Card className="card-modern">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-bold text-foreground">Recent Calls</h2>
          </div>
          <div className="divide-y divide-[#27272A]">
            {calls.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No calls yet. Start a call or wait for incoming calls.
              </div>
            ) : (
              calls.map((call) => (
                <div
                  key={call.call_id}
                  data-testid={`call-row-${call.call_id}`}
                  className="p-4 hover:bg-white/5 cursor-pointer transition-colors flex items-center justify-between"
                  onClick={() => viewCallDetails(call.call_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-foreground font-medium">
                        {call.from_number || 'Web Call'}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {formatDate(call.start_timestamp)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Quick Play Button */}
                    {call.recording_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          playRecording(call.recording_url, call.call_id);
                        }}
                        className="w-8 h-8 p-0 rounded-full hover:bg-cyan-500/20"
                        data-testid={`quick-play-${call.call_id}`}
                      >
                        {playingRecording === call.call_id ? (
                          <Pause className="w-4 h-4 text-cyan-400" />
                        ) : (
                          <Play className="w-4 h-4 text-cyan-400" />
                        )}
                      </Button>
                    )}
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(call.call_status)}`}>
                        {call.call_status}
                      </span>
                      <p className="text-muted-foreground text-sm mt-1">
                        {formatDuration(call.duration_ms)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Call Details Modal */}
      <Dialog open={!!selectedCall} onOpenChange={() => { setSelectedCall(null); setCallDetails(null); }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground">Call Details</DialogTitle>
          </DialogHeader>
          
          {callDetails ? (
            <div className="space-y-4 mt-4">
              {/* Recording Player - Enhanced */}
              {callDetails.recording_url ? (
                <AudioPlayer 
                  recordingUrl={callDetails.recording_url} 
                  duration={callDetails.duration_ms}
                  callId={callDetails.call_id}
                />
              ) : (
                <div className="p-4 rounded-lg bg-muted border border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-500/20 flex items-center justify-center">
                      <Volume2 className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">No Recording Available</p>
                      <p className="text-xs text-muted-foreground">Recording may not be enabled for this call</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Call Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded bg-muted">
                  <p className="text-muted-foreground text-xs">From</p>
                  <p className="text-foreground">{callDetails.from_number || 'Web Call'}</p>
                </div>
                <div className="p-3 rounded bg-muted">
                  <p className="text-muted-foreground text-xs">Duration</p>
                  <p className="text-foreground">{formatDuration(callDetails.duration_ms)}</p>
                </div>
                <div className="p-3 rounded bg-muted">
                  <p className="text-muted-foreground text-xs">Status</p>
                  <p className={`${callDetails.call_status === 'ended' ? 'text-green-400' : 'text-gray-400'}`}>
                    {callDetails.call_status}
                  </p>
                </div>
                <div className="p-3 rounded bg-muted">
                  <p className="text-muted-foreground text-xs">Time</p>
                  <p className="text-foreground">{formatDate(callDetails.start_timestamp)}</p>
                </div>
              </div>

              {/* Call Analysis */}
              {callDetails.call_analysis && (
                <div className="p-4 rounded bg-muted">
                  <p className="text-muted-foreground text-xs mb-2">Pro Analysis</p>
                  {typeof callDetails.call_analysis === 'object' ? (
                    <dl className="space-y-2 text-sm" data-testid="call-analysis-fields">
                      {Object.entries({ ...callDetails.call_analysis, ...(callDetails.call_analysis.custom_analysis_data || {}) })
                        .filter(([k, v]) => k !== 'custom_analysis_data' && v !== null && v !== '' && typeof v !== 'object')
                        .map(([k, v]) => (
                          <div key={k} className="grid grid-cols-3 gap-2">
                            <dt className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</dt>
                            <dd className="col-span-2 text-foreground">{String(v)}</dd>
                          </div>
                        ))}
                    </dl>
                  ) : (
                    <p className="text-foreground text-sm">{callDetails.call_analysis}</p>
                  )}
                </div>
              )}

              <CallLookups callId={callDetails.call_id} />

              {/* Transcript */}
              <div>
                <p className="text-muted-foreground text-sm mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Transcript
                </p>
                <div className="bg-muted rounded p-4 max-h-64 overflow-y-auto">
                  {callDetails.transcript ? (
                    typeof callDetails.transcript === 'string' ? (
                      <p className="text-foreground text-sm whitespace-pre-wrap">{callDetails.transcript}</p>
                    ) : (
                      <div className="space-y-2">
                        {callDetails.transcript.map((item, i) => (
                          <div key={i} className={`p-2 rounded ${item.role === 'user' ? 'bg-red-500/10 border-l-2 border-red-500' : 'bg-blue-500/10 border-l-2 border-blue-500'}`}>
                            <span className={`text-xs ${item.role === 'user' ? 'text-red-400' : 'text-blue-400'}`}>
                              {item.role === 'user' ? 'CALLER' : 'PRO'}
                            </span>
                            <p className="text-foreground text-sm">{item.content}</p>
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    <p className="text-muted-foreground text-sm">No transcript available</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="spinner" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallHistory;
